import React, { useState, useEffect } from 'react';
import { getImageUrls } from '../utils/imageUtils';

/**
 * Simplified ImageLoader for debugging image display issues
 */
const SimpleImageLoader = ({ photo, size = 'medium', className = '', alt, height = '200px' }) => {
  const [imageState, setImageState] = useState({
    loading: true,
    error: false,
    currentUrl: ''
  });

  useEffect(() => {
    if (!photo) {
      setImageState({ loading: false, error: true, currentUrl: '' });
      return;
    }

    const urlsResult = getImageUrls(photo);
    const url = urlsResult[size] || urlsResult.medium || urlsResult.large || urlsResult.thumb || '';
    
    if (!url) {
      setImageState({ loading: false, error: true, currentUrl: '' });
      return;
    }

    setImageState({ loading: true, error: false, currentUrl: url });
  }, [photo, size]);

  const handleImageLoad = () => {
    setImageState(prev => ({ ...prev, loading: false, error: false }));
  };

  const handleImageError = () => {
    setImageState(prev => ({ ...prev, loading: false, error: true }));
  };

  const generateAltText = () => {
    if (alt) return alt;
    const aircraftType = photo?.aircraftType || 'Aircraft';
    const registration = photo?.registration || 'Unknown registration';
    return `${aircraftType} - ${registration}`;
  };

  if (!imageState.currentUrl) {
    return (
      <div className={`simple-image-loader error ${className}`} style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: height, 
        backgroundColor: '#f0f0f0',
        color: '#666'
      }}>
        <div>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📷</div>
          <div>No image available</div>
        </div>
      </div>
    );
  }

  if (imageState.loading) {
    return (
      <div className={`simple-image-loader loading ${className}`} style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: height, 
        backgroundColor: '#f0f0f0',
        color: '#666'
      }}>
        <div>
          <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>⏳</div>
          <div>Loading image...</div>
        </div>
        <img
          src={imageState.currentUrl}
          alt={generateAltText()}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  if (imageState.error) {
    return (
      <div className={`simple-image-loader error ${className}`} style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: height, 
        backgroundColor: '#f0f0f0',
        color: '#666'
      }}>
        <div>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚠️</div>
          <div>Failed to load image</div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageState.currentUrl}
      alt={generateAltText()}
      className={`simple-image-loader success ${className}`}
      onLoad={handleImageLoad}
      onError={handleImageError}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

export default SimpleImageLoader;